import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "../design/components/Button";
import { Dialog } from "../design/components/Dialog";
import { Reason } from "../design/components/Reason";
import { messageForError } from "../i18n/errors";
import { useT } from "../i18n/locale";
import { tasksSearchOfDialer } from "../tasks/task-search";
import { useDialerSession, type DialerSummaryShown } from "./dialer-session";
import { dialerLeadName, passoverText, sourceText, summaryLeftText } from "./dialer-texts";
import { useSourcePeople } from "./source-people";

// Il riepilogo della sessione (piano M5, Task 5, D6; mock-up `scheda-lead.html`, `m-session-summary`): dopo «Interrompi», dopo
// una sessione interrotta dal server (il battito perso) e alla fine della lista. I numeri vengono dal server (Lead chiamati,
// chiamate, con risposta, minuti al telefono, saltati da chi chiama: con «Salta» e passati al successivo senza chiamare), poi
// i saltati con il motivo, quelli che si saltano da soli e quanti restano nella lista. Interrotta, la sessione resta salvata: «Continua a chiamare» la riprende subito, «Torna alla lista» apre la lista,
// dove «Riprendi a chiamare» la riprende anche un altro giorno; dai Task (piano M5, Task 21) «Torna ai Task» riapre la stessa scheda
// e la stessa persona. Disco e demo prenotate arrivano con M6 (questione 254).

export function SummaryDialog({ shown }: { shown: DialerSummaryShown }) {
  const t = useT();
  const navigate = useNavigate();
  const { closeSummary, start, timeZone } = useDialerSession();
  const [problem, setProblem] = useState<unknown>(null);
  const [pending, setPending] = useState(false);
  const { summary } = shown;
  const people = useSourcePeople(shown.tab, shown.source);
  const stats = [
    { label: t("dialer.summaryStats.leadsCalled"), value: summary.leadsCalled },
    { label: t("dialer.summaryStats.calls"), value: summary.calls },
    { label: t("dialer.summaryStats.answered"), value: summary.answered },
    {
      label: t("dialer.summaryStats.minutes"),
      value: Math.ceil(summary.talkSeconds / 60),
    },
    {
      // Come nel mock-up, chi è passato al successivo senza chiamare conta fra i saltati da te (D5).
      label: t("dialer.summaryStats.skipped"),
      value: summary.skippedByUser.length + summary.passedWithoutCall.length,
    },
  ];

  // «Torna alla lista» riapre la Smart List; dai Task (piano M5, Task 21) «Torna ai Task» riapre la stessa scheda e la stessa persona.
  const fromTasks = shown.source.type === "task_view";
  // Il nome della sorgente: per i Task dal catalogo, nella lingua di chi guarda, con la persona (la barra dice lo stesso); per una Smart
  // List quello che aveva quando la sessione è nata. Le parole dei Task non hanno virgolette attorno al nome.
  const sourceName = fromTasks ? sourceText(t, shown, people) : shown.sourceName;
  const words = fromTasks ? "Tasks" : "";
  function backToSource() {
    closeSummary();
    if (shown.source.type === "smart_list")
      void navigate({
        to: "/smart-list/$smartListId",
        params: { smartListId: shown.source.smartListId },
      });
    else void navigate({ to: "/task", search: tasksSearchOfDialer(shown.source) });
  }

  async function keepCalling() {
    setPending(true);
    setProblem(null);
    try {
      await start({ tab: shown.tab, source: shown.source, mode: "resume" });
    } catch (error) {
      setProblem(error);
      setPending(false);
    }
  }

  return (
    <Dialog
      open
      title={summary.finished ? t(`dialer.summaryFinishedTitle${words}`) : t("dialer.summaryTitle")}
      onClose={closeSummary}
      footer={
        <>
          {summary.finished ? null : (
            <Button disabled={pending} onClick={() => void keepCalling()}>
              {t("dialer.continue")}
            </Button>
          )}
          <Button variant="primary" onClick={backToSource}>
            {fromTasks ? t("tasks.backToTasks") : t("dialer.backToList")}
          </Button>
        </>
      }
    >
      <div className="g-stack">
        {!summary.finished && summary.next ? <Reason>{t(`dialer.summarySaved${words}`, { next: dialerLeadName(summary.next) })}</Reason> : null}
        <p>{t(summary.finished ? `dialer.summaryFinished${words}` : `dialer.summaryOf${words}`, { source: sourceName })}</p>
        <dl className="g-dialer-stats">
          {stats.map((stat) => (
            <div key={stat.label}>
              <dt>{stat.label}</dt>
              <dd>{stat.value}</dd>
            </div>
          ))}
        </dl>
        {summary.skippedByUser.length + summary.passedWithoutCall.length > 0 ? (
          <div>
            <p className="g-dialer-stats__heading">{t("dialer.summarySkipped")}</p>
            <ul className="g-dialer-queue__list">
              {summary.skippedByUser.map((item) => (
                <li key={item.lead.id}>
                  {t("dialer.withReason", {
                    lead: dialerLeadName(item.lead),
                    reason: t(`dialer.skipReasons.${item.reason}`),
                  })}
                </li>
              ))}
              {summary.passedWithoutCall.map((lead) => (
                <li key={lead.id}>
                  {t("dialer.withReason", {
                    lead: dialerLeadName(lead),
                    reason: t("dialer.queueResults.passed"),
                  })}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {summary.passedOver.length > 0 ? (
          <div>
            <p className="g-dialer-stats__heading">{t("dialer.summaryPassedOver")}</p>
            <ul className="g-dialer-queue__list">
              {summary.passedOver.map((lead) => (
                <li key={lead.id}>
                  {lead.passover
                    ? t("dialer.withReason", {
                        lead: dialerLeadName(lead),
                        reason: passoverText(t, lead.passover, timeZone),
                      })
                    : dialerLeadName(lead)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <p className="g-dialer-stats__left">{summaryLeftText(t, summary.remaining, summary.passedOver.length, fromTasks)}</p>
        {problem ? (
          <p className="g-field__error" role="alert">
            {messageForError(t, problem)}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
